import React, { useContext } from 'react';
import { Route, Switch } from 'react-router-dom';
import { usePendoInitialization } from 'pendo/pendoUtils';
import { PARKING_SCENES, ROAD_SCENES } from '../static/constants/scenes';
import PATH_ROUTES from '../static/constants/path-routes';
import { indexByBrand } from 'shared/utils';
import { BRAND } from 'shared/utils';
import { IndividualStateKey, Brands, ScreenIndex, LocationIndex } from '../context/interfaces';
import activeGuard from './ActiveGuard';
import Survey from './survey/Survey';
import DamagesScreen from './damages/Damages';
import NarrationScreen from './insured/Narration';
import SketchScreen from './insured/Sketch';
import LocationScreen from './location/Location';
import CameraScreen from './photos/CameraScreen';
import ClientInfoScreen from './policy/ClientInfo';
import SplashScreen from './splash/Splash';
import SuccessScreen from './success/Success';
import ResponsibilityScreen from './summary/Responsibility';
import SummaryScreen from './summary/Summary';
import RightsFormScreen from './rightsForm';
import InsuranceAlertScreen from './insured/InsuranceAlert';
import IsVehicleScreen from './isVehicle/IsVehicle';
import PolicivePartScreen from './summary/PolicivePart';
import UploadPhotoScreen from './photos/UploadPhoto';
import InactiveScreen from './inactive/Inactive';
import IsDepositScreen from './deposit/IsDeposit';
import DirectDepositScreen from './deposit/DirectDeposit';
import SceneScreen from './scene';
import ScenePhotosScreen from './scene/scene-photos';
import PhotoSectionsMenu from './photos/PhotoSectionsMenu/PhotoSectionsMenu';
import VideoUploadScreen from './photos/video/VideoUploadScreen';
import AlertDeviceScreen from '../screens/alertDevice/AlertDeviceScreen';
import StoryScreen from './insured/story';
import AdditionalDocs from './additionalDocs/AdditionalDocs';
import Certification from './certification/Certification';
import { Context } from 'context/Context';
import { AppContext } from 'context/interfaces';

// Used to render a white screen before validating guard.
const RENDER_BLANK = true;

/** Siguiente ruta tras mapa de ubicación u "otra ubicación" (evita ternarios anidados). */
function getLocationMapNextPath(): string {
	if ([Brands.ASSA, Brands.LAFISE, Brands.OCEANICA].includes(BRAND as Brands)) {
		return '/:id/insured/narration';
	}
	if (BRAND === Brands.QUALITAS || BRAND === Brands.INS) {
		return '/:id/insured/story';
	}
	if (BRAND === Brands.PURDY) {
		return '/:id/insured/storyPurdy';
	}
	return '/:id/scene';
}

const ScreensRoot = (): JSX.Element => {
	const ctx = useContext(Context) as AppContext;
	const { ClaimantType, serviceId } = ctx;

	usePendoInitialization();

	console.log('Root-ServiceId :: ', serviceId);

	const title =
		Brands.CSM === BRAND ||
		Brands.PREMIER === BRAND ||
		Brands.MULTINATIONAL === BRAND ||
		Brands.OPTIMA === BRAND ||
		Brands.SODA === BRAND
			? 'Valide Información de Perjudicado'
			: 'Valide Información Asegurado';

	return (
		<>
			{[Brands.PREMIER, Brands.OPTIMA, Brands.MULTINATIONAL, Brands.SODA].includes(BRAND as Brands) ? (
				<Switch>
					{/* COMMON path routes */}
					<Route
						path="/:id/insured/information"
						key="/insured/information" // key is used to remount the component
						render={(): JSX.Element =>
							activeGuard(
								<ClientInfoScreen
									screenKey={indexByBrand.insuredInformation}
									title={title}
									to="/:id/driver/information"
								/>
							)
						}
					/>
					<Route
						path="/:id/driver/information"
						key="/driver/information" // key is used to remount the component
						render={(): JSX.Element =>
							activeGuard(
								<ClientInfoScreen
									screenKey={indexByBrand.driverInformation}
									title={'Valide información del Conductor'}
									to="/:id/vehicle/information"
								/>
							)
						}
					/>
					<Route
						path="/:id/vehicle/information"
						key="/vehicle/information" // key is used to remount the component
						render={(): JSX.Element =>
							activeGuard(
								<ClientInfoScreen
									screenKey={indexByBrand.vehicleInformation}
									title={
										ClaimantType === 'Asegurado' || ClaimantType?.toUpperCase() === 'INSURED'
											? 'Valide vehículo del Asegurado'
											: 'Valide vehículo del Perjudicado'
									}
									to={'/:id/injured/narration'}
								/>
							)
						}
					/>
					<Route
						path="/:id/location/select"
						key="/location/select"
						render={(): JSX.Element => {
							const toPage = '/:id/location';
							return activeGuard(
								<LocationScreen
									locationIndex={LocationIndex.INSPECT_SITE}
									to={toPage}
									screenIndex={indexByBrand.locationSelect}
								/>
							);
						}}
					/>
					<Route
						path="/:id/location"
						key="/location"
						render={(): JSX.Element => {
							const toPage = '/:id/scene';
							return activeGuard(
								<LocationScreen
									to={toPage}
									locationIndex={LocationIndex.LOCATION_MAP}
									screenIndex={indexByBrand.location}
								/>
							);
						}}
					/>
					<Route
						path="/:id/other-location"
						key="/other-location"
						render={(): JSX.Element =>
							activeGuard(
								<LocationScreen
									locationIndex={LocationIndex.OTHER_LOCATION}
									to="/:id/scene"
									screenIndex={indexByBrand.location}
								/>
							)
						}
					/>
					<Route
						path="/:id/counterpart/information"
						key="/counterpart/information"
						render={(): JSX.Element =>
							activeGuard(
								<ClientInfoScreen
									screenKey={indexByBrand.counterPartInformation}
									isCounterpartInfo
									title="Información de vehículo de la contraparte"
									to={'/:id/counterpart/damages'}
								/>
							)
						}
					/>
					<Route
						path="/:id/counterpart/damages"
						key="/counterpart/damages"
						render={(): JSX.Element =>
							activeGuard(
								<DamagesScreen
									title="Contraparte"
									screenKey={indexByBrand.counterPartDamages}
									to="/:id/photo/sections"
								/>
							)
						}
					/>
					<Route
						path="/:id/insured/sketch"
						key="/insured/sketch"
						render={(): JSX.Element =>
							activeGuard(
								<SketchScreen
									to={
										ClaimantType === 'Asegurado' || ClaimantType?.toUpperCase() === 'INSURED'
											? '/:id/insured/narration'
											: '/:id/injured/narration'
									}
									screenIndex={indexByBrand.sceneLocation}
								/>
							)
						}
					/>
					<Route
						path="/:id/insured/narration"
						key="/insured/narration"
						render={(): JSX.Element =>
							activeGuard(
								<NarrationScreen
									to={'/:id/insured/damages'}
									screenKey={indexByBrand.insuredNarration}
									stateKey={IndividualStateKey.INSURED}
								/>
							)
						}
					/>
					<Route
						path="/:id/insured/damages"
						key="/insured/damages"
						render={(): JSX.Element =>
							activeGuard(
								<DamagesScreen
									title="Asegurado"
									screenKey={indexByBrand.insuredDamages}
									to={'/:id/injured/location/select'}
								/>
							)
						}
					/>
					<Route
						path="/:id/policivePart"
						key="/policivePart"
						render={(): JSX.Element => {
							const to = '/:id/location';

							return activeGuard(<PolicivePartScreen to={to} screenIndex={indexByBrand.policivePart} />);
						}}
					/>
					<Route
						path="/:id/photos/camera"
						key="/photos/camera"
						render={(): JSX.Element => activeGuard(<CameraScreen screenIndex={0} />)}
					/>
					<Route
						path="/:id/camera/video"
						key="/camera/video"
						render={(): JSX.Element => activeGuard(<VideoUploadScreen />)}
					/>
					<Route
						path="/:id/summary"
						key="/summary"
						render={(): JSX.Element =>
							activeGuard(<SummaryScreen to={'/:id/success'} screenIndex={indexByBrand.summary} />)
						}
					/>
					{/* /success doesn't use activeGuard */}
					<Route path="/:id/success" key="/success" component={SuccessScreen} />
					<Route
						path="/:id/insured/responsibility"
						key="/insured/responsibility"
						render={(): JSX.Element =>
							activeGuard(
								<ResponsibilityScreen to={'/:id/insured/damages'} screenIndex={indexByBrand.insuredResponsability} />
							)
						}
					/>
					<Route
						path="/:id/scene/location"
						key="/scene/location"
						render={(): JSX.Element =>
							activeGuard(<SketchScreen to={'/:id/summary'} screenIndex={indexByBrand.sceneLocation} />)
						}
					/>
					<Route
						path="/:id/scene/parking"
						key="/scene/parking"
						render={(): JSX.Element =>
							activeGuard(<ScenePhotosScreen photos={PARKING_SCENES} screenIndex={indexByBrand.scene} />)
						}
					/>
					<Route
						path="/:id/scene/road"
						key="/scene/road"
						render={(): JSX.Element =>
							activeGuard(<ScenePhotosScreen photos={ROAD_SCENES} screenIndex={indexByBrand.scene} />)
						}
					/>
					<Route
						path="/:id/scene"
						key="/scene"
						render={(): JSX.Element =>
							activeGuard(<SceneScreen locationIndex={LocationIndex.SCENE} screenIndex={indexByBrand.scene} />)
						}
					/>
					<Route
						path="/:id/injured/information"
						key="/injured/information" // key is used to remount the component
						render={(): JSX.Element =>
							activeGuard(
								<ClientInfoScreen
									screenKey={indexByBrand.injuredInformation}
									title={title}
									to="/:id/driver/information"
								/>
							)
						}
					/>
					<Route
						path="/:id/injured/narration"
						key="/injured/narration"
						render={(): JSX.Element =>
							activeGuard(
								<NarrationScreen
									to="/:id/injured/damages"
									screenKey={indexByBrand.injuredNarration}
									stateKey={IndividualStateKey.INJURED}
								/>
							)
						}
					/>
					<Route
						path="/:id/injured/damages"
						key="/injured/damages"
						render={(): JSX.Element =>
							activeGuard(
								<DamagesScreen
									title="Perjudicado"
									screenKey={indexByBrand.injuredDamages}
									to="/:id/injured/location/select"
								/>
							)
						}
					/>
					<Route
						path="/:id/injured/location/select"
						key="/injured/location/select"
						render={(): JSX.Element =>
							activeGuard(
								<LocationScreen
									to="/:id/injured/location"
									locationIndex={LocationIndex.SELECT_LOCATION}
									screenIndex={indexByBrand.injuredLocationSelect}
								/>
							)
						}
					/>
					<Route
						path="/:id/injured/location"
						key="/injured/location"
						render={(): JSX.Element =>
							activeGuard(
								<LocationScreen
									to="/:id/scene"
									locationIndex={LocationIndex.LOCATION_MAP}
									screenIndex={indexByBrand.injuredLocation}
								/>
							)
						}
					/>
					<Route
						path="/:id/injured/sketch"
						key="/injured/sketch"
						render={(): JSX.Element =>
							activeGuard(<SketchScreen to="/:id/summary" screenIndex={indexByBrand.sceneLocation} />)
						}
					/>
					<Route
						path="/:id/validate/documents"
						key="/validate/documents"
						render={(): JSX.Element => activeGuard(<AdditionalDocs />)}
					/>
					<Route
						path="/:id/photo/sections"
						key="/photo/sections"
						render={(): JSX.Element =>
							activeGuard(
								<PhotoSectionsMenu
									to={
										ClaimantType === 'Asegurado' || ClaimantType?.toUpperCase() === 'INSURED'
											? '/:id/insured/information'
											: '/:id/injured/information'
									}
									screenIndex={indexByBrand.photoSection}
								/>
							)
						}
					/>
					<Route
						path="/:id/injured/photos/camera"
						key="/injured/photos/camera"
						render={(): JSX.Element => activeGuard(<CameraScreen screenIndex={0} />)}
					/>
					<Route
						path="/:id/injured/deposit"
						key="/injured/deposit"
						render={(): JSX.Element =>
							activeGuard(
								<IsDepositScreen
									to="/:id/summary"
									locationIndex={LocationIndex.IS_DEPOSIT}
									screenIndex={indexByBrand.isDeposit}
								/>
							)
						}
					/>
					<Route
						path="/:id/deposit/form"
						key="/deposit/form"
						render={(): JSX.Element =>
							activeGuard(<DirectDepositScreen to="/:id/summary" screenIndex={indexByBrand.depositForm} />)
						}
					/>
					<Route
						path="/:id/rights-form"
						key="/rights-form"
						render={(): JSX.Element =>
							activeGuard(
								<RightsFormScreen
									to={
										[Brands.MULTINATIONAL, Brands.PREMIER, Brands.OPTIMA, Brands.SODA].includes(BRAND as Brands)
											? BRAND === Brands.PREMIER || BRAND === Brands.SODA
												? '/:id/certification'
												: '/:id/survey'
											: '/:id/success'
									}
								/>
							)
						}
					/>
					<Route
						path="/:id/certification"
						key="/certification"
						render={(): JSX.Element => activeGuard(<Certification to="/:id/survey" />)}
					/>
					<Route path="/:id/inactive" key="/inactive" component={InactiveScreen} />
					<Route path={PATH_ROUTES.PRE_ALERT_DEVICE_SCREEN} key="/alert" component={AlertDeviceScreen} />
					<Route
						path="/:id/survey"
						key="survey"
						render={(): JSX.Element => activeGuard(<Survey screenKey={indexByBrand.survey} to="/:id/success" />)}
					/>
					<Route path="/:id" key="/:id" render={(): JSX.Element => activeGuard(<SplashScreen />, RENDER_BLANK)} />
				</Switch>
			) : (
				<Switch>
					{/* COMMON path routes */}
					<Route
						path="/:id/insured/information"
						key="/insured/information" // key is used to remount the component
						render={(): JSX.Element =>
							activeGuard(
								<ClientInfoScreen
									screenKey={indexByBrand.insuredInformation}
									title={
										ClaimantType === 'Asegurado' || ClaimantType?.toUpperCase() === 'INSURED'
											? 'Valide información del Asegurado'
											: 'Valide información del Perjudicado'
									}
									to="/:id/driver/information"
								/>
							)
						}
					/>
					<Route
						path="/:id/driver/information"
						key="/driver/information" // key is used to remount the component
						render={(): JSX.Element =>
							activeGuard(
								<ClientInfoScreen
									screenKey={indexByBrand.driverInformation}
									title={
										[Brands.LAFISE, Brands.OCEANICA, Brands.QUALITAS, Brands.INS].includes(BRAND as Brands)
											? 'Datos del Conductor'
											: 'Valide información del Conductor'
									}
									to="/:id/vehicle/information"
								/>
							)
						}
					/>
					<Route
						path="/:id/vehicle/information"
						key="/vehicle/information" // key is used to remount the component
						render={(): JSX.Element =>
							activeGuard(
								<ClientInfoScreen
									screenKey={indexByBrand.vehicleInformation}
									title={
										BRAND === Brands.CSM ||
										BRAND === Brands.MULTINATIONAL ||
										Brands.PREMIER === BRAND ||
										Brands.SODA === BRAND
											? 'Valide vehículo del Perjudicado'
											: 'Valide vehículo del asegurado'
									}
									to={
										BRAND === Brands.CSM ||
										BRAND === Brands.MULTINATIONAL ||
										Brands.PREMIER === BRAND ||
										Brands.SODA === BRAND ||
										Brands.OPTIMA === BRAND
											? '/:id/injured/narration'
											: BRAND === Brands.SEGUROS_MUNDIAL
												? '/:id/validation'
												: '/:id/location/select'
									}
								/>
							)
						}
					/>
					<Route
						path="/:id/location/select"
						key="/location/select"
						render={(): JSX.Element => {
							const toPage = [
								Brands.ASSA,
								Brands.IS_CR,
								Brands.LAFISE,
								Brands.OCEANICA,
								Brands.QUALITAS,
								Brands.CONNECT,
								Brands.PURDY,
								Brands.INS,
							].includes(BRAND as Brands)
								? `/:id/${BRAND}/isVehicle`
								: '/:id/location';
							return activeGuard(
								<LocationScreen
									locationIndex={LocationIndex.INSPECT_SITE}
									to={toPage}
									screenIndex={indexByBrand.locationSelect}
								/>
							);
						}}
					/>
					<Route
						path="/:id/validation"
						key="/validation"
						render={(): JSX.Element =>
							activeGuard(
								<ClientInfoScreen
									screenKey={indexByBrand.validation}
									title={' Validación para el Propietario del Vehículo Afectado'}
									to={'/:id/accident/information'}
								/>
							)
						}
					/>
					<Route
						path="/:id/accident/information"
						key="/accident/information"
						render={(): JSX.Element =>
							activeGuard(
								<LocationScreen
									locationIndex={LocationIndex.OTHER_LOCATION}
									to={'/:id/insured/narration'}
									screenIndex={indexByBrand.accidentInformation}
								/>
							)
						}
					/>
					<Route
						path="/:id/location"
						key="/location"
						render={(): JSX.Element => {
							const toPage = getLocationMapNextPath();
							return activeGuard(
								<LocationScreen
									to={toPage}
									locationIndex={LocationIndex.LOCATION_MAP}
									screenIndex={indexByBrand.location}
								/>
							);
						}}
					/>
					<Route
						path="/:id/other-location"
						key="/other-location"
						render={(): JSX.Element => {
							const to = getLocationMapNextPath();
							return activeGuard(
								<LocationScreen
									locationIndex={LocationIndex.OTHER_LOCATION}
									to={to}
									screenIndex={indexByBrand.location}
								/>
							);
						}}
					/>
					<Route
						path="/:id/counterpart/information"
						key="/counterpart/information"
						render={(): JSX.Element =>
							activeGuard(
								<ClientInfoScreen
									screenKey={indexByBrand.counterPartInformation}
									isCounterpartInfo
									title="Información de vehículo de la contraparte"
									to={
										[
											Brands.LAFISE,
											Brands.OCEANICA,
											Brands.QUALITAS,
											Brands.ASSA,
											Brands.IS_CR,
											Brands.CONNECT,
											Brands.PURDY,
											Brands.INS,
										].includes(BRAND as Brands)
											? '/:id/policivePart'
											: '/:id/counterpart/damages'
									}
								/>
							)
						}
					/>
					<Route
						path="/:id/counterpart/damages"
						key="/counterpart/damages"
						render={(): JSX.Element =>
							activeGuard(
								<DamagesScreen
									title="Contraparte"
									screenKey={indexByBrand.counterPartDamages}
									to="/:id/photo/sections"
								/>
							)
						}
					/>
					<Route
						path="/:id/insured/sketch"
						key="/insured/sketch"
						render={(): JSX.Element =>
							activeGuard(
								<SketchScreen
									to={
										ClaimantType === 'Asegurado' || ClaimantType?.toUpperCase() === 'INSURED'
											? '/:id/insured/narration'
											: '/:id/injured/narration'
									}
									screenIndex={indexByBrand.sceneLocation}
								/>
							)
						}
					/>
					<Route
						path="/:id/insured/narration"
						key="/insured/narration"
						render={(): JSX.Element =>
							activeGuard(
								<NarrationScreen
									to={
										BRAND === Brands.LAFISE
											? '/:id/lafise/insured/insurance-alert'
											: BRAND === Brands.SURA
												? '/:id/insured/responsibility'
												: BRAND === Brands.OCEANICA
													? '/:id/oceanica/insured/insurance-alert'
													: '/:id/insured/damages'
									}
									screenKey={indexByBrand.insuredNarration}
									stateKey={IndividualStateKey.INSURED}
								/>
							)
						}
					/>
					<Route
						path="/:id/insured/story"
						key="/insured/story"
						render={(): JSX.Element => {
							const to =
								BRAND === Brands.INS
									? '/:id/ins/insured/insurance-alert'
									: '/:id/qualitas/insured/insurance-alert';
							return activeGuard(
								<StoryScreen
									to={to}
									screenKey={indexByBrand.insuredNarration}
									stateKey={IndividualStateKey.INSURED}
								/>
							);
						}}
					/>
					<Route
						path="/:id/insured/storyPurdy"
						key="/insured/story"
						render={(): JSX.Element =>
							activeGuard(
								<NarrationScreen
									to={'/:id/insured/damages'}
									screenKey={indexByBrand.insuredNarration}
									stateKey={IndividualStateKey.INSURED}
								/>
							)
						}
					/>
					<Route
						path="/:id/insured/damages"
						key="/insured/damages"
						render={(): JSX.Element =>
							activeGuard(
								<DamagesScreen
									title="Asegurado"
									screenKey={indexByBrand.insuredDamages}
									to={
										[
											Brands.CONNECT,
											Brands.LAFISE,
											Brands.OCEANICA,
											Brands.QUALITAS,
											Brands.ASSA,
											Brands.IS_CR,
											Brands.SEGUROS_MUNDIAL,
											Brands.COMFENALCO,
											Brands.PURDY,
											Brands.INS,
										].includes(BRAND as Brands)
											? '/:id/photo/sections'
											: '/:id/counterpart/information'
									}
								/>
							)
						}
					/>

					<Route
						path="/:id/policivePart"
						key="/policivePart"
						render={(): JSX.Element => {
							const to = [Brands.QUALITAS, Brands.LAFISE, Brands.OCEANICA, Brands.ASSA, Brands.PURDY, Brands.INS, Brands.IS_CR].includes(BRAND as Brands)
								? `/:id/${BRAND}/location`
								: '/:id/location';

							return activeGuard(<PolicivePartScreen to={to} screenIndex={indexByBrand.policivePart} />);
						}}
					/>

					<Route
						path="/:id/photos/camera"
						key="/photos/camera"
						render={(): JSX.Element => activeGuard(<CameraScreen screenIndex={0} />)}
					/>
					<Route
						path="/:id/camera/video"
						key="/camera/video"
						render={(): JSX.Element => activeGuard(<VideoUploadScreen />)}
					/>
					<Route
						path="/:id/summary"
						key="/summary"
						render={(): JSX.Element =>
							activeGuard(
								<SummaryScreen
									to={'/:id/success'}
									screenIndex={indexByBrand.summary}
								/>
							)
						}
					/>
					{/* /success doesn't use activeGuard */}
					<Route path="/:id/success" key="/success" component={SuccessScreen} />

					{/* CONNECT / SURA path routes */}
					<Route
						path="/:id/sura/insured/information"
						key="/sura/insured/information" // key is used to remount the component
						render={(): JSX.Element =>
							activeGuard(<ClientInfoScreen screenKey={ScreenIndex.INSURED} title={title} to="/:id/location" />)
						}
					/>

					<Route
						path="/:id/insured/responsibility"
						key="/insured/responsibility"
						render={(): JSX.Element =>
							activeGuard(
								<ResponsibilityScreen
									to={
										[Brands.LAFISE, Brands.OCEANICA, Brands.QUALITAS, Brands.INS].includes(BRAND as Brands)
											? '/:id/photos/camera'
											: '/:id/insured/damages'
									}
									screenIndex={indexByBrand.insuredResponsability}
								/>
							)
						}
					/>

					<Route
						path="/:id/scene/location"
						key="/scene/location"
						render={(): JSX.Element =>
							activeGuard(
								<SketchScreen
									to={
										BRAND === Brands.CSM ||
										BRAND === Brands.MULTINATIONAL ||
										BRAND === Brands.PREMIER ||
										BRAND === Brands.SODA
											? '/:id/photo/sections'
											: '/:id/insured/narration'
									}
									screenIndex={indexByBrand.sceneLocation}
								/>
							)
						}
					/>

					<Route
						path="/:id/scene/parking"
						key="/scene/parking"
						render={(): JSX.Element =>
							activeGuard(<ScenePhotosScreen photos={PARKING_SCENES} screenIndex={indexByBrand.scene} />)
						}
					/>

					<Route
						path="/:id/scene/road"
						key="/scene/road"
						render={(): JSX.Element =>
							activeGuard(<ScenePhotosScreen photos={ROAD_SCENES} screenIndex={indexByBrand.scene} />)
						}
					/>
					<Route
						path="/:id/scene"
						key="/scene"
						render={(): JSX.Element =>
							activeGuard(<SceneScreen locationIndex={LocationIndex.SCENE} screenIndex={indexByBrand.scene} />)
						}
					/>

					{/* CSM path routes */}
					<Route
						path="/:id/injured/information"
						key="/injured/information" // key is used to remount the component
						render={(): JSX.Element =>
							activeGuard(
								<ClientInfoScreen
									screenKey={indexByBrand.injuredInformation}
									title={title}
									to="/:id/driver/information"
								/>
							)
						}
					/>
					<Route
						path="/:id/injured/narration"
						key="/injured/narration"
						render={(): JSX.Element =>
							activeGuard(
								<NarrationScreen
									to="/:id/injured/damages"
									screenKey={indexByBrand.injuredNarration}
									stateKey={IndividualStateKey.INJURED}
								/>
							)
						}
					/>
					<Route
						path="/:id/injured/damages"
						key="/injured/damages"
						render={(): JSX.Element =>
							activeGuard(
								<DamagesScreen
									title="Perjudicado"
									screenKey={indexByBrand.injuredDamages}
									to="/:id/injured/location/select"
								/>
							)
						}
					/>
					<Route
						path="/:id/injured/location/select"
						key="/injured/location/select"
						render={(): JSX.Element =>
							activeGuard(
								<LocationScreen
									to="/:id/injured/location"
									locationIndex={LocationIndex.SELECT_LOCATION}
									screenIndex={indexByBrand.injuredLocationSelect}
								/>
							)
						}
					/>
					<Route
						path="/:id/injured/location"
						key="/injured/location"
						render={(): JSX.Element =>
							activeGuard(
								<LocationScreen
									to="/:id/scene"
									locationIndex={LocationIndex.LOCATION_MAP}
									screenIndex={indexByBrand.injuredLocation}
								/>
							)
						}
					/>
					<Route
						path="/:id/injured/sketch"
						key="/injured/sketch"
						render={(): JSX.Element =>
							activeGuard(<SketchScreen to="/:id/photo/sections" screenIndex={indexByBrand.sceneLocation} />)
						}
					/>
					<Route
						path="/:id/photo/sections"
						key="/photo/sections"
						render={(): JSX.Element =>
							activeGuard(
								<PhotoSectionsMenu
									to={
										BRAND === Brands.LAFISE
											? '/:id/lafise/uploadPhoto'
											: BRAND === Brands.ASSA
												? '/:id/assa/uploadPhoto'
												: BRAND === Brands.IS_CR
													? '/:id/is_cr/uploadPhoto'
													: BRAND === Brands.OCEANICA
													? '/:id/oceanica/uploadPhoto'
													: BRAND === Brands.QUALITAS
														? '/:id/qualitas/uploadPhoto'
														: BRAND === Brands.CONNECT
															? '/:id/connect/uploadPhoto'
															: BRAND === Brands.SEGUROS_MUNDIAL
																? '/:id/segurosmundial/uploadPhoto'
																: BRAND === Brands.PURDY
																	? '/:id/purdy/uploadPhoto'
																	: BRAND === Brands.INS
																		? '/:id/ins/uploadPhoto'
																		: '/:id/summary'
									}
									screenIndex={indexByBrand.photoSection}
								/>
							)
						}
					/>
					<Route
						path="/:id/validate/documents"
						key="/validate/documents"
						render={(): JSX.Element => activeGuard(<AdditionalDocs />)}
					/>
					<Route
						path="/:id/injured/photos/camera"
						key="/injured/photos/camera"
						render={(): JSX.Element => activeGuard(<CameraScreen screenIndex={0} />)}
					/>
					<Route
						path="/:id/injured/deposit"
						key="/injured/deposit"
						render={(): JSX.Element =>
							activeGuard(
								<IsDepositScreen
									to="/:id/summary"
									locationIndex={LocationIndex.IS_DEPOSIT}
									screenIndex={indexByBrand.isDeposit}
								/>
							)
						}
					/>
					<Route
						path="/:id/deposit/form"
						key="/deposit/form"
						render={(): JSX.Element =>
							activeGuard(<DirectDepositScreen to="/:id/summary" screenIndex={indexByBrand.depositForm} />)
						}
					/>

					<Route
						path="/:id/rights-form"
						key="/rights-form"
						render={(): JSX.Element =>
							activeGuard(
								<RightsFormScreen
									to={
										[
											Brands.CSM,
											Brands.SEGUROS_MUNDIAL,
											Brands.MULTINATIONAL,
											Brands.PREMIER,
											Brands.COMFENALCO,
											Brands.OPTIMA,
											Brands.SEGUROS_MUNDIAL,
											Brands.SODA,
										].includes(BRAND as Brands)
											? BRAND === Brands.PREMIER || BRAND === Brands.SODA
												? '/:id/certification'
												: '/:id/survey'
											: '/:id/success'
									}
								/>
							)
						}
					/>
					<Route
						path="/:id/certification"
						key="/certification"
						render={(): JSX.Element => activeGuard(<Certification to="/:id/survey" />)}
					/>
					<Route
						path="/:id/connect/isVehicle"
						key="/connect/isVehicle"
						render={(): JSX.Element =>
							activeGuard(
								<IsVehicleScreen
									to="/:id/counterpart/information"
									locationIndex={LocationIndex.IS_VEHICLE}
									screenIndex={indexByBrand.isVehicle}
								/>
							)
						}
					/>
					<Route
						path="/:id/connect/uploadPhoto"
						key="/connect/uploadPhoto"
						render={(): JSX.Element =>
							activeGuard(<UploadPhotoScreen to="/:id/summary" screenIndex={indexByBrand.uploadPhoto} />)
						}
					/>

					{/* LAFISE path routes */}
					<Route
						path="/:id/lafise/location"
						key="/lafise/location"
						render={(): JSX.Element =>
							activeGuard(
								<LocationScreen
									to="/:id/insured/narration"
									locationIndex={LocationIndex.LOCATION_MAP}
									screenIndex={indexByBrand.location}
								/>
							)
						}
					/>
					<Route
						path="/:id/lafise/insured/insurance-alert"
						key="/lafise/insured/insurance-alert"
						render={(): JSX.Element =>
							activeGuard(
								<InsuranceAlertScreen
									to="/:id/insured/damages"
									stateKey={IndividualStateKey.INSURED}
									screenKey={indexByBrand.insuranceAlert}
								/>
							)
						}
					/>
					<Route
						path="/:id/lafise/isVehicle"
						key="/lafise/isVehicle"
						render={(): JSX.Element =>
							activeGuard(
								<IsVehicleScreen
									to="/:id/counterpart/information"
									locationIndex={LocationIndex.IS_VEHICLE}
									screenIndex={indexByBrand.isVehicle}
								/>
							)
						}
					/>
					<Route
						path="/:id/lafise/uploadPhoto"
						key="/lafise/uploadPhoto"
						render={(): JSX.Element =>
							activeGuard(<UploadPhotoScreen to="/:id/summary" screenIndex={indexByBrand.uploadPhoto} />)
						}
					/>
					<Route
						path="/:id/assa/isVehicle"
						key="/assa/isVehicle"
						render={(): JSX.Element =>
							activeGuard(
								<IsVehicleScreen
									to="/:id/counterpart/information"
									locationIndex={LocationIndex.IS_VEHICLE}
									screenIndex={indexByBrand.isVehicle}
								/>
							)
						}
					/>
					<Route
						path="/:id/assa/uploadPhoto"
						key="/assa/uploadPhoto"
						render={(): JSX.Element =>
							activeGuard(<UploadPhotoScreen to="/:id/summary" screenIndex={indexByBrand.uploadPhoto} />)
						}
					/>
					<Route
						path="/:id/is_cr/isVehicle"
						key="/is_cr/isVehicle"
						render={(): JSX.Element =>
							activeGuard(
								<IsVehicleScreen
									to="/:id/counterpart/information"
									locationIndex={LocationIndex.IS_VEHICLE}
									screenIndex={indexByBrand.isVehicle}
								/>
							)
						}
					/>
					<Route
						path="/:id/is_cr/uploadPhoto"
						key="/is_cr/uploadPhoto"
						render={(): JSX.Element =>
							activeGuard(<UploadPhotoScreen to="/:id/summary" screenIndex={indexByBrand.uploadPhoto} />)
						}
					/>
					<Route
						path="/:id/is_cr/location"
						key="/is_cr/location"
						render={(): JSX.Element =>
							activeGuard(
								<LocationScreen
									to="/:id/insured/narration"
									locationIndex={LocationIndex.LOCATION_MAP}
									screenIndex={indexByBrand.location}
								/>
							)
						}
					/>

					{/* QUALITAS path routes */}
					<Route
						path="/:id/qualitas/location"
						key="/qualitas/location"
						render={(): JSX.Element =>
							activeGuard(
								<LocationScreen
									to="/:id/insured/story"
									locationIndex={LocationIndex.LOCATION_MAP}
									screenIndex={indexByBrand.location}
								/>
							)
						}
					/>
					<Route
						path="/:id/qualitas/insured/insurance-alert"
						key="/qualitas/insured/insurance-alert"
						render={(): JSX.Element =>
							activeGuard(
								<InsuranceAlertScreen
									to="/:id/insured/damages"
									stateKey={IndividualStateKey.INSURED}
									screenKey={indexByBrand.insuredAlert}
								/>
							)
						}
					/>
					<Route
						path="/:id/qualitas/isVehicle"
						key="/qualitas/isVehicle"
						render={(): JSX.Element =>
							activeGuard(
								<IsVehicleScreen
									to="/:id/counterpart/information"
									locationIndex={LocationIndex.IS_VEHICLE}
									screenIndex={indexByBrand.isVehicle}
								/>
							)
						}
					/>
					<Route
						path="/:id/qualitas/uploadPhoto"
						key="/qualitas/uploadPhoto"
						render={(): JSX.Element =>
							activeGuard(<UploadPhotoScreen to="/:id/summary" screenIndex={indexByBrand.uploadPhoto} />)
						}
					/>

					{/* PURDY path routes */}
					<Route
						path="/:id/purdy/location"
						key="/purdy/location"
						render={(): JSX.Element =>
							activeGuard(
								<LocationScreen
									to="/:id/insured/storyPurdy"
									locationIndex={LocationIndex.LOCATION_MAP}
									screenIndex={indexByBrand.location}
								/>
							)
						}
					/>
					<Route
						path="/:id/purdy/insured/insurance-alert"
						key="/purdy/insured/insurance-alert"
						render={(): JSX.Element =>
							activeGuard(
								<InsuranceAlertScreen
									to="/:id/insured/damages"
									stateKey={IndividualStateKey.INSURED}
									screenKey={indexByBrand.insuredAlert}
								/>
							)
						}
					/>
					<Route
						path="/:id/purdy/isVehicle"
						key="/purdy/isVehicle"
						render={(): JSX.Element =>
							activeGuard(
								<IsVehicleScreen
									to="/:id/counterpart/information"
									locationIndex={LocationIndex.IS_VEHICLE}
									screenIndex={indexByBrand.isVehicle}
								/>
							)
						}
					/>
					<Route
						path="/:id/purdy/uploadPhoto"
						key="/purdy/uploadPhoto"
						render={(): JSX.Element =>
							activeGuard(<UploadPhotoScreen to="/:id/summary" screenIndex={indexByBrand.uploadPhoto} />)
						}
					/>

					{/* INS path routes */}
					<Route
						path="/:id/ins/location"
						key="/ins/location"
						render={(): JSX.Element =>
							activeGuard(
								<LocationScreen
									to="/:id/insured/story"
									locationIndex={LocationIndex.LOCATION_MAP}
									screenIndex={indexByBrand.location}
								/>
							)
						}
					/>
					<Route
						path="/:id/ins/insured/insurance-alert"
						key="/ins/insured/insurance-alert"
						render={(): JSX.Element =>
							activeGuard(
								<InsuranceAlertScreen
									to="/:id/insured/damages"
									stateKey={IndividualStateKey.INSURED}
									screenKey={indexByBrand.insuredAlert}
								/>
							)
						}
					/>
					<Route
						path="/:id/ins/isVehicle"
						key="/ins/isVehicle"
						render={(): JSX.Element =>
							activeGuard(
								<IsVehicleScreen
									to="/:id/counterpart/information"
									locationIndex={LocationIndex.IS_VEHICLE}
									screenIndex={indexByBrand.isVehicle}
								/>
							)
						}
					/>
					<Route
						path="/:id/ins/uploadPhoto"
						key="/ins/uploadPhoto"
						render={(): JSX.Element =>
							activeGuard(<UploadPhotoScreen to="/:id/summary" screenIndex={indexByBrand.uploadPhoto} />)
						}
					/>

					{/* OCEANICA path routes */}
					<Route
						path="/:id/oceanica/location"
						key="/oceanica/location"
						render={(): JSX.Element =>
							activeGuard(
								<LocationScreen
									to="/:id/insured/narration"
									locationIndex={LocationIndex.LOCATION_MAP}
									screenIndex={indexByBrand.location}
								/>
							)
						}
					/>
					<Route
						path="/:id/oceanica/insured/insurance-alert"
						key="/oceanica/insured/insurance-alert"
						render={(): JSX.Element =>
							activeGuard(
								<InsuranceAlertScreen
									to="/:id/insured/damages"
									stateKey={IndividualStateKey.INSURED}
									screenKey={indexByBrand.insuranceAlert}
								/>
							)
						}
					/>
					<Route
						path="/:id/oceanica/isVehicle"
						key="/oceanica/isVehicle"
						render={(): JSX.Element =>
							activeGuard(
								<IsVehicleScreen
									to="/:id/counterpart/information"
									locationIndex={LocationIndex.IS_VEHICLE}
									screenIndex={indexByBrand.isVehicle}
								/>
							)
						}
					/>
					<Route
						path="/:id/oceanica/uploadPhoto"
						key="/oceanica/uploadPhoto"
						render={(): JSX.Element =>
							activeGuard(<UploadPhotoScreen to="/:id/summary" screenIndex={indexByBrand.uploadPhoto} />)
						}
					/>
					<Route
						path="/:id/segurosmundial/uploadPhoto"
						key="/segurosmundial/uploadPhoto"
						render={(): JSX.Element =>
							activeGuard(<UploadPhotoScreen to="/:id/summary" screenIndex={indexByBrand.uploadPhoto} />)
						}
					/>
					<Route path="/:id/inactive" key="/inactive" component={InactiveScreen} />
					<Route path={PATH_ROUTES.PRE_ALERT_DEVICE_SCREEN} key="/alert" component={AlertDeviceScreen} />
					<Route
						path="/:id/survey"
						key="survey"
						render={(): JSX.Element => activeGuard(<Survey screenKey={indexByBrand.survey} to="/:id/success" />)}
					/>
					<Route path="/:id" key="/:id" render={(): JSX.Element => activeGuard(<SplashScreen />, RENDER_BLANK)} />
				</Switch>
			)}
		</>
	);
};

export default ScreensRoot;
